var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { motion } from "motion/react";
import { useState, useEffect } from "react";
import { loadRecipe } from "../../utils/recipeApi";
import { transformRecipe } from "../../utils/recipeTransform";
export function PairingsSection({ pairings, onPairingClick }) {
    const [pairingRecipes, setPairingRecipes] = useState(new Map());
    // Load recipes for all pairings
    useEffect(() => {
        const loadPairingRecipes = () => __awaiter(this, void 0, void 0, function* () {
            const newMap = new Map();
            // Initialize all pairings as loading
            pairings.forEach(pairing => {
                newMap.set(pairing.slug, {
                    slug: pairing.slug,
                    recipe: null,
                    loading: true,
                });
            });
            setPairingRecipes(newMap);
            // Load each recipe
            for (const pairing of pairings) {
                try {
                    const sourceData = yield loadRecipe(pairing.slug);
                    if (sourceData) {
                        // Check if it's a DetailedRecipe
                        const isDetailed = 'ingredients_1f' in sourceData || 'ingredients_2f' in sourceData ||
                            'ingredients_3f' in sourceData || 'get_set' in sourceData ||
                            'safety_notes' in sourceData;
                        let recipe;
                        if (isDetailed) {
                            recipe = sourceData;
                        }
                        else {
                            const transformed = transformRecipe(sourceData);
                            recipe = transformed || null;
                        }
                        setPairingRecipes(prev => {
                            const updated = new Map(prev);
                            updated.set(pairing.slug, {
                                slug: pairing.slug,
                                recipe,
                                loading: false,
                            });
                            return updated;
                        });
                    }
                    else {
                        setPairingRecipes(prev => {
                            const updated = new Map(prev);
                            updated.set(pairing.slug, {
                                slug: pairing.slug,
                                recipe: null,
                                loading: false,
                            });
                            return updated;
                        });
                    }
                }
                catch (error) {
                    console.error(`Failed to load pairing recipe ${pairing.slug}:`, error);
                    setPairingRecipes(prev => {
                        const updated = new Map(prev);
                        updated.set(pairing.slug, {
                            slug: pairing.slug,
                            recipe: null,
                            loading: false,
                        });
                        return updated;
                    });
                }
            }
        });
        if (pairings.length > 0) {
            loadPairingRecipes();
        }
    }, [pairings]);
    if (pairings.length === 0)
        return null;
    const handlePairingClick = (slug) => __awaiter(this, void 0, void 0, function* () {
        const pairingData = pairingRecipes.get(slug);
        if (pairingData === null || pairingData === void 0 ? void 0 : pairingData.recipe) {
            onPairingClick(pairingData.recipe);
        }
        else {
            // Try to load on demand if not already loaded
            try {
                const sourceData = yield loadRecipe(slug);
                if (sourceData) {
                    const isDetailed = 'ingredients_1f' in sourceData || 'ingredients_2f' in sourceData ||
                        'ingredients_3f' in sourceData || 'get_set' in sourceData ||
                        'safety_notes' in sourceData;
                    const recipe = isDetailed ? sourceData : transformRecipe(sourceData);
                    if (recipe) {
                        onPairingClick(recipe);
                    }
                }
            }
            catch (error) {
                console.error(`Failed to load recipe ${slug}:`, error);
            }
        }
    });
    return (_jsxs(motion.div, { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 }, className: "bg-[#faf8f5] border border-[#e8e3db] rounded-lg p-6 recipe-print-section", children: [_jsx("h2", { className: "mb-4 text-[#5a4a3a]", children: "Pairs Well With" }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-3 print:grid-cols-1", children: pairings.map((pairing, index) => {
                    const pairingData = pairingRecipes.get(pairing.slug);
                    const recipe = pairingData === null || pairingData === void 0 ? void 0 : pairingData.recipe;
                    const isLoading = pairingData === null || pairingData === void 0 ? void 0 : pairingData.loading;
                    // Calculate total time
                    let totalTime = 0;
                    let imageUrl;
                    if (recipe) {
                        if ('cook_time_total_minutes' in recipe && 'prep_time_minutes' in recipe) {
                            totalTime = (recipe.cook_time_total_minutes || 0) + (recipe.prep_time_minutes || 0);
                        }
                        else if ('cookTime' in recipe && 'prepTime' in recipe) {
                            totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0);
                        }
                        // Extract image URL
                        if ('image' in recipe) {
                            if (typeof recipe.image === 'string') {
                                imageUrl = recipe.image;
                            }
                            else if (recipe.image && typeof recipe.image === 'object') {
                                imageUrl = recipe.image.full || recipe.image.catalog || recipe.image.thumbnail;
                            }
                        }
                        else if ('image' in recipe) {
                            imageUrl = recipe.image;
                        }
                    }
                    return (_jsxs("button", { onClick: () => handlePairingClick(pairing.slug), disabled: isLoading, className: "flex items-center gap-3 p-3 rounded-lg border border-[#e8e3db] hover:border-[#a8c5a4] hover:bg-white transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed print:border-0 print:p-0 print:block page-break-avoid", children: [isLoading ? (_jsx("div", { className: "w-16 h-16 bg-[#e8e3db] rounded flex-shrink-0 animate-pulse print:hidden" })) : imageUrl ? (_jsx("img", { src: imageUrl, alt: pairing.title, className: "w-16 h-16 object-cover rounded flex-shrink-0 print:hidden" })) : (_jsx("div", { className: "w-16 h-16 bg-[#e8e3db] rounded flex-shrink-0 print:hidden" })), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("h3", { className: "text-[#5a4a3a] group-hover:text-[#a8c5a4] transition-colors", children: pairing.title }), isLoading ? (_jsx("p", { className: "text-sm text-[#5a4a3a]/60", children: "Loading..." })) : totalTime > 0 ? (_jsxs("p", { className: "text-sm text-[#5a4a3a]/60", children: [totalTime, " min"] })) : null] })] }, index));
                }) })] }));
}
